import { useState, useRef, useEffect, useCallback } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import { useDispatch } from "react-redux";
import {
  setActiveLongTextCell,
  setErrorFields,
} from "../../../store/tableSlice";
import { cn } from "../../../lib/utils";
import useOutsideClick from "../../../hooks/useOutsideClick";

// Enhanced tooltip and editor for long text fields
export const LongTextCell = ({
  value,
  isEditable,
  onSave,
  onCancel,
  rowId,
  columnId,
  maxStringLength,
  columnInfo,
  setOptimisticValue,
  optimisticValue,
  isError,
  isRequired,
  isEmpty,
  errorFields,
}) => {
  // Handle null or undefined values
  const safeValue = value === null || value === undefined ? "" : String(value);

  const [isEditing, setIsEditing] = useState(false);
  const cellRef = useRef(null);

  const dispatch = useDispatch();

  // Helper function to handle error state updates
  const updateErrorState = (newValue) => {
    if (newValue) {
      // Value is not empty, remove field from errorFields
      const updatedErrorFields = errorFields
        .map((error) => {
          if (error.rowId === rowId) {
            const filteredFields = error.fields.filter(
              (field) => field !== columnId
            );
            return { ...error, fields: filteredFields };
          }
          return error;
        })
        .filter((error) => error.fields.length > 0);

      dispatch(setErrorFields(updatedErrorFields));
    } else if (isRequired) {
      // Value is empty and field is required
      const existingError = errorFields.find((error) => error.rowId === rowId);
      if (existingError) {
        const updatedFields = Array.from(
          new Set([...existingError.fields, columnId])
        );
        dispatch(
          setErrorFields(
            errorFields.map((error) =>
              error.rowId === rowId
                ? { ...error, fields: updatedFields }
                : error
            )
          )
        );
      } else {
        dispatch(
          setErrorFields([
            ...errorFields,
            {
              rowId: rowId,
              fields: [columnId],
            },
          ])
        );
      }
    }
  };

  // Replace the original onChange handler with our own that updates Redux
  const handleChange = (value) => {
    setOptimisticValue(value);
    updateErrorState(value);
  };

  const handleCancel = useCallback(() => {
    onCancel?.();
    setOptimisticValue(safeValue);
    setIsEditing(false);
  }, [onCancel, safeValue, setOptimisticValue]);

  const handleSave = async () => {
    setIsEditing(false);

    try {
      const result = await onSave?.(optimisticValue);

      // Update error state based on the save result
      if (!result?.error) {
        updateErrorState(optimisticValue);
      } else {
        setOptimisticValue(safeValue);
      }
    } catch (error) {
      setOptimisticValue(safeValue);
      console.log("Error saving long text:", error);
    }
  };

  // Use the outside click hook to auto-save when clicking outside
  const textareaRef = useOutsideClick(handleSave, isEditing);

  useEffect(() => {
    if (!isEditing) {
      dispatch(setActiveLongTextCell(null));
      return;
    }

    dispatch(setActiveLongTextCell({ rowId, columnId }));

    return () => {
      // Clean up the active cell when unmounting while editing
      if (isEditing) {
        dispatch(setActiveLongTextCell(null));
      }
    };
  }, [isEditing, dispatch, rowId, columnId]);

  if (isEditing) {
    return (
      <div
        ref={textareaRef}
        className="z-50"
        style={{
          position: "absolute",
          inset: "0px auto auto 0px",
          // The transform will be set dynamically via the useEffect
        }}
      >
        <div
          className="flex flex-col bg-white border border-gray-200 rounded p-2"
          style={{
            width: "300px",
            minWidth: "200px",
            minHeight: "107px",
            height: "107px",
            resize: "both",
            overflow: "auto",
            pointerEvents: "all",
            maxHeight: "25em",
            maxWidth: "50em",
            paddingTop: "8px",
            paddingRight: "8px",
          }}
        >
          <textarea
            maxLength={maxStringLength}
            autoFocus
            dir="auto"
            value={optimisticValue}
            onChange={(e) => handleChange(e.target.value)}
            className="flex-grow outline-none border-none w-full h-full p-2 resize-none"
            placeholder="Enter text here..."
          />
          <div className="flex justify-end mt-1 text-xs text-gray-500">
            <div>
              {optimisticValue.length}/{maxStringLength}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Create display content based on whether there's text or not
  const hasContent = optimisticValue.length > 0;
  const displayText =
    hasContent && optimisticValue.length > 50
      ? `${optimisticValue.substring(0, 50)}...`
      : hasContent
      ? optimisticValue
      : columnInfo.display;

  return (
    <div
      ref={cellRef}
      className={cn(
        "cursor-pointer",
        isEditable &&
          "hover:border hover:border-blue-300 hover:bg-blue-50 rounded p-px",
        isError && "border border-red-500 bg-red-50"
      )}
      onClick={() => isEditable && setIsEditing(true)}
    >
      {isRequired && isEmpty && !isEditing && (
        <span
          className="text-red-500 mr-1 align-middle"
          title="This is a required field."
        >
          *
        </span>
      )}
      {hasContent ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>{displayText}</span>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              align="center"
              className="font-medium w-64 max-h-64 overflow-auto bg-gray-900 text-gray-200 border border-gray-700 shadow-xl rounded-md p-3"
            >
              <div className="space-y-4 text-sm">
                {optimisticValue.split("\n\n").map((paragraph, index) => (
                  <p key={index} className={index > 0 ? "mt-4" : ""}>
                    {paragraph}
                  </p>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <span className="text-gray-400">{displayText}</span>
      )}
    </div>
  );
};
